import { ChevronDown, ChevronUp, Zap, Shield, Clock, Users, Lightbulb, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

export default function SEOSections() {
  const [openFAQ, setOpenFAQ] = useState<number | null>(0);

  const features = [
    {
      icon: Zap,
      title: 'Lightning Fast',
      description: 'Zero-latency execution. All tools process data directly in your browser for peak efficiency.'
    },
    {
      icon: Shield,
      title: 'Private & Secure',
      description: 'Your data never leaves your device. We use client-side processing for maximum privacy and security.'
    },
    {
      icon: Clock,
      title: 'Time Saving',
      description: 'Optimize your workflow with high-precision utilities designed to execute complex tasks in seconds.'
    },
    {
      icon: Users,
      title: 'For Everyone',
      description: 'Engineered for developers, designers, students, and professionals across all industries.'
    },
    {
      icon: Lightbulb,
      title: 'Smart Features',
      description: 'Advanced capabilities and intuitive presets designed to handle both simple and complex requirements.'
    },
    {
      icon: Sparkles,
      title: 'AI Powered',
      description: 'Leveraging modern web technologies and smart algorithms to deliver accurate, high-quality results.'
    },
  ];

  const faqItems = [
    {
      question: 'Are these tools really free?',
      answer: `Yes. Every tool in the Vibe Tools collection is 100% free for unrestricted use. We believe in providing accessible, high-performance utilities to everyone.`
    },
    {
      question: 'What kind of tools are available?',
      answer: 'Our collection spans multiple categories including PDF management, Image editing, Developer utilities (JSON, SQL, Regex), Network diagnostics, and more.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Absolutely. All processing occurs locally within your browser. Data is never uploaded to our servers, ensuring your information remains completely private.'
    },
    {
      question: 'Are there tools for SEO experts?',
      answer: 'Yes. We offer a suite of SEO tools including Meta Tag Analyzers, Robots.txt Testers, Sitemap Generators, and Keyword tools to help optimize your web presence.'
    },
    {
      question: 'How can I support Vibe Tools?',
      answer: 'The best way to support us is by sharing the platform with your colleagues and friends. We appreciate your help in growing our community.'
    },
    {
      question: 'How do I request a new tool?',
      answer: 'We value user feedback. If you have a specific tool request or suggestion, please feel free to reach out through our contact channels.'
    },
  ];

  return (
    <div className="bg-white dark:bg-slate-950 transition-colors duration-500">
      {/* Features Section */}
      <section className="py-24 px-6 border-t border-gray-100 dark:border-white/5 bg-gray-50/50 dark:bg-slate-900/50">
        <div className="max-w-[1240px] mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-extrabold mb-6 text-gray-900 dark:text-white tracking-tight leading-tight">
              Why Choose <span className="text-primary">Vibe Tools</span>?
            </h2>
            <p className="text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed font-medium">
              Experience a suite of professional-grade utilities built for speed, privacy, and modern reliability.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="bg-white dark:bg-slate-900 p-8 rounded-2xl shadow-sm border border-gray-100 dark:border-white/5 hover:shadow-xl dark:hover:shadow-primary/5 transition-all duration-300 group">
                  <div className="w-12 h-12 bg-primary/5 dark:bg-primary/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-primary transition-colors duration-300">
                    <Icon className="w-6 h-6 text-primary group-hover:text-white transition-colors duration-300" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-black text-gray-900 dark:text-gray-100 mb-3 tracking-tight">{feature.title}</h3>
                  <p className="text-gray-500 dark:text-gray-400 leading-relaxed font-medium">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Deployment/CTA Section */}
      <section className="py-24 px-6 border-y border-gray-100 dark:border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 bg-primary/5 dark:bg-primary/10 -z-10"></div>
        <div className="max-w-[1240px] mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-extrabold mb-6 text-gray-900 dark:text-white tracking-tight leading-tight">
            Ready to Boost Your <span className="text-primary">Productivity?</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed font-medium">
            Access our complete collection of developer and creative utilities. Secure, fast, and always free.
          </p>

          <Link
            to="/all-tools"
            className="inline-flex items-center justify-center px-8 py-4 bg-primary text-white font-bold rounded-full hover:bg-primary/95 transition-all shadow-lg shadow-primary/20 hover:shadow-primary/30 transform hover:-translate-y-1"
          >
            Explore All Tools
            <Zap className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 px-6 bg-white dark:bg-slate-950">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-gray-900 dark:text-white tracking-tight leading-tight">
              Frequently Asked Questions
            </h2>
            <p className="text-gray-500 dark:text-gray-400 font-medium">Everything you need to know about our platform.</p>
          </div>

          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="border border-gray-200 dark:border-white/10 rounded-2xl bg-white dark:bg-slate-900 overflow-hidden hover:border-primary/30 transition-all duration-300 shadow-sm hover:shadow-xl dark:hover:shadow-primary/5">
                <button
                  onClick={() => setOpenFAQ(openFAQ === index ? null : index)}
                  className="w-full flex items-center justify-between p-6 text-left"
                >
                  <span className={`text-base font-bold transition-colors ${openFAQ === index ? 'text-primary' : 'text-gray-900 dark:text-gray-100'}`}>{item.question}</span>
                  {openFAQ === index ? (
                    <ChevronUp className="w-5 h-5 text-primary flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 dark:text-gray-600 flex-shrink-0" />
                  )}
                </button>
                <AnimatePresence>
                  {openFAQ === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                    >
                      <div className="px-6 pb-6 pt-0">
                        <p className="text-gray-600 dark:text-gray-400 leading-relaxed border-t border-gray-100 dark:border-white/5 pt-4 font-medium">
                          {item.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
